// Accept an invite: set a password, optionally name + timezone, then land
// authenticated. Email arrives with ?code= on the emailed link; SMS has no
// link to click, so a texted code is typed in here instead.

import { ApiClientError, inviteAddress, verifyInvite } from '../api/client.js';
import { navigate, type View } from '../router.js';
import { el } from '../dom.js';
import { tzPicker } from './tzPicker.js';
import { passwordReveal } from './passwordReveal.js';
import { isValidTz, TZ_SIGNUP_DEFAULT } from '@msc/shared';

export const verifyView: View = (outlet, ctx) => {
  const linkCode = ctx.query.get('code') ?? '';

  const error = el('p', { class: 'form-error', hidden: true });
  const submit = el('button', { type: 'submit', class: 'btn' }, 'Set password and continue');

  // No email field visible on this form -- without an explicit
  // autocomplete="username" field, Chrome's save-password heuristic picks the
  // last text input before the password field as the username, which is
  // lastName. Purely visual CSS hiding (not `hidden`/display:none, not
  // tabindex="-1" -- both disqualify a field from Chrome's form parser
  // entirely rather than just hiding it). An EMPTY field here just stops the
  // wrong-guess bug: Chrome declines to prompt at all rather than guess. To
  // get the save-password prompt back with the RIGHT value, the address
  // fetched below actually fills it in.
  const usernameHint = el('input', {
    type: 'text',
    name: 'username',
    autocomplete: 'username',
    style: 'position:absolute;width:1px;height:1px;overflow:hidden;left:-9999px;',
  }) as HTMLInputElement;

  // Best-effort: the SPA has no other way to know the invite's address until
  // this resolves. If it's still empty by the time the user submits, that's
  // fine -- see the comment above. Only worth trying once we actually have a
  // code -- there isn't one yet on a bare /verify SMS lands the user on.
  if (linkCode) {
    void inviteAddress(linkCode).then((address) => {
      if (address) usernameHint.value = address;
    });
  }

  // SMS has no link, so there is no ?code= to read -- the user types the
  // texted code in here instead. Left hidden when a link already supplied one.
  const codeInput = el('input', {
    id: 'code',
    name: 'code',
    type: 'text',
    inputmode: 'numeric',
    autocomplete: 'one-time-code',
    placeholder: '6-digit code',
  }) as HTMLInputElement;
  const codeRow = el('div', { class: 'field', hidden: !!linkCode }, [
    el('label', { for: 'code' }, 'Verification code'),
    codeInput,
  ]);

  const firstName = el('input', {
    id: 'firstName',
    name: 'firstName',
    type: 'text',
    autocomplete: 'given-name',
  }) as HTMLInputElement;

  const lastName = el('input', {
    id: 'lastName',
    name: 'lastName',
    type: 'text',
    autocomplete: 'family-name',
  }) as HTMLInputElement;

  const password = el('input', {
    id: 'password',
    name: 'password',
    type: 'password',
    required: true,
    autocomplete: 'new-password',
    minlength: 8,
  }) as HTMLInputElement;

  // NO inherit row here. Every other picker in the app offers one, because there
  // "blank" means a real thing: follow the group's zone, or the site's. On this
  // form it means nothing a stranger can act on — they have never heard of the
  // site zone, and the SPA cannot even show them what it is (see tzDisplayLabel
  // in @msc/shared). Will, seeing it on this form: "I've never heard of the site
  // default time zone."
  //
  // So this picker always holds a real IANA id: the browser's zone where it
  // reports a usable one, and `TZ_SIGNUP_DEFAULT` — Mountain — where it does not.
  // `autodetect` is not used, because it only fires on an EMPTY picker and the
  // fallback below fills it; seeding the value here keeps the precedence
  // (browser first, fallback second) visible in one expression instead of split
  // across two files.
  const zone = tzPicker({
    name: 'timeZone',
    value: browserZone() ?? TZ_SIGNUP_DEFAULT,
    allowInherit: false,
    id: 'timeZone',
  });

  const form = el('form', { method: 'post', action: '/verify', novalidate: true }, [
    usernameHint,
    codeRow,
    el('label', { for: 'firstName' }, 'First name'),
    firstName,
    el('label', { for: 'lastName' }, 'Last name'),
    lastName,
    el('label', { for: 'password' }, 'Password (at least 8 characters)'),
    password,
    // This form asks for the password once, so this is the only chance to read
    // it back before it is committed. See passwordReveal.ts.
    passwordReveal(password),
    // AFTER the password, deliberately. The hidden `usernameHint` above exists
    // because Chrome's save-password heuristic otherwise picks the last text
    // input before the password field as the username, and it had picked
    // lastName. The tz picker's search box is a text input, so putting it above
    // the password would make it the new candidate — the explicit
    // autocomplete="username" field should still win, but re-testing a
    // hard-won password-manager fix is not worth the field order.
    el('label', { for: 'timeZone' }, 'Your timezone'),
    zone.root,
    el(
      'p',
      { class: 'muted small' },
      "We guessed from your browser. This only affects how times are shown to " +
        'you. It never changes when an event is scheduled.',
    ),
    error,
    submit,
  ]) as HTMLFormElement;

  form.addEventListener('submit', async (ev) => {
    ev.preventDefault();
    error.hidden = true;
    const code = linkCode || codeInput.value.trim();
    if (!code) {
      error.textContent = 'Enter the code we texted or emailed you.';
      error.hidden = false;
      return;
    }
    submit.setAttribute('disabled', 'disabled');
    try {
      await verifyInvite({
        code,
        password: password.value,
        firstName: firstName.value.trim() || undefined,
        lastName: lastName.value.trim() || undefined,
        // Always a real IANA id — this picker has no empty row.
        timeZone: zone.value(),
      });
      // Every new account passes through here, which is what makes this the
      // first-run trigger: no flag records that onboarding happened.
      navigate('/welcome', true);
    } catch (err) {
      error.textContent =
        err instanceof ApiClientError ? err.message : 'Something went wrong. Try again.';
      error.hidden = false;
      submit.removeAttribute('disabled');
    }
  });

  outlet.append(el('main', { class: 'auth-card' }, [el('h1', {}, 'Finish signing up'), form]));
};

/**
 * The browser's own timezone, when it reports one this app can actually store.
 *
 * Guarded twice on purpose. `Intl` can throw or return undefined on a stripped
 * or misconfigured runtime, and it can also report a zone that is real to the
 * browser but not canonical to us — either of which, taken at face value, would
 * become a stored value the picker cannot display back and the server would
 * reject. Returning null here hands the decision to the caller's fallback.
 */
function browserZone(): string | null {
  try {
    const guess = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return guess && isValidTz(guess) ? guess : null;
  } catch {
    return null;
  }
}
